import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";
import { stores } from "src/App";

// Extend the InternalAxiosRequestConfig & AxiosRequestConfig interface to include additional properties
declare module "axios" {
    export interface InternalAxiosRequestConfig {
        /** This is the sentryTranscation that gets started by `getRequestInterceptorSentryTransactionStart` and finished by `getResponseInterceptorSuccessSentryTranscationFinish`, `getResponseInterceptorErrorSentryTranscationFinish` or `getRequestInterceptorErrorSentryTransactionFinish`   */
        sentryTransaction?: ReturnType<Sentry.Hub["startTransaction"]>;

        /** Set this in order to group sentry transactions without the params */
        urlWithoutParams?: string;
    }

    export interface AxiosRequestConfig {
        /** Set this in order to group sentry transactions without the params */
        urlWithoutParams?: string;
    }
}

const HEADER_AUTH = "Authorization";
const DEFAULT_TIMEOUT = 6000 * 10; // 60 seconds

class Axios {
    private static instance?: AxiosInstance = undefined;
    private static authInstance?: AxiosInstance = undefined;

    public static getInstance() {
        if (!Axios.instance) {
            Axios.instance = axios.create();
            Axios.instance.defaults.timeout = DEFAULT_TIMEOUT;
            Axios.instance.interceptors.request.use(
                Axios.getRequestInterceptorSentryTransactionStart(),
                Axios.getRequestInterceptorErrorSentryTransactionFinish(),
                {
                    synchronous: true
                }
            );

            Axios.instance.interceptors.response.use(
                Axios.getResponseInterceptorSuccessSentryTranscationFinish(),
                Axios.getResponseInterceptorErrorSentryTranscationFinish()
            );
        }

        return Axios.instance;
    }

    public static getAuthInstance() {
        if (!Axios.authInstance) {
            Axios.authInstance = axios.create();
            Axios.authInstance.defaults.timeout = DEFAULT_TIMEOUT;
            Axios.authInstance.interceptors.request.use(
                Axios.getRequestInterceptorSentryTransactionStart(),
                Axios.getRequestInterceptorErrorSentryTransactionFinish(),
                {
                    synchronous: true
                }
            );

            Axios.authInstance.interceptors.request.use(Axios.getRequestInterceptorToken(), null, {
                synchronous: true
            });

            Axios.authInstance.interceptors.response.use(
                Axios.getResponseInterceptorSuccessSentryTranscationFinish(),
                Axios.getResponseInterceptorErrorSentryTranscationFinish()
            );

            Axios.authInstance.interceptors.response.use(
                Axios.getResponseInterceptor401Success(),
                Axios.getResponseInterceptor401Error()
            );

            // Registered last so it only sees errors the 401/refresh interceptor did not
            // resolve. Makes backend auth (403) and server (5xx) failures visible in Sentry.
            Axios.authInstance.interceptors.response.use(
                (response: AxiosResponse<any, any>) => response,
                Axios.getResponseInterceptorErrorCapture()
            );
        }

        return Axios.authInstance;
    }

    private static getRequestInterceptorToken() {
        return (config: InternalAxiosRequestConfig) => {
            if (stores.authStore.token) {
                config.headers[HEADER_AUTH] = `${stores.authStore.tokenType} ${stores.authStore.token}`;
            }
            return config;
        };
    }

    private static getRequestInterceptorSentryTransactionStart() {
        return (config: InternalAxiosRequestConfig) => {
            let urlToLog = config.urlWithoutParams ? config.urlWithoutParams : config.url;
            config.sentryTransaction = Sentry.startTransaction({
                name: `${urlToLog?.includes(".wwportal.ch/") ? urlToLog.split(".wwportal.ch/")[1] : urlToLog}`,
                op: "Axios Request"
            });
            return config;
        };
    }

    private static getRequestInterceptorErrorSentryTransactionFinish() {
        return (error: any) => {
            error?.config?.sentryTransaction?.finish();
            return error;
        };
    }

    private static getResponseInterceptorSuccessSentryTranscationFinish() {
        return (response: AxiosResponse<any, any>) => {
            response.config?.sentryTransaction?.finish();
            return response;
        };
    }

    private static getResponseInterceptorErrorSentryTranscationFinish() {
        return (error: any) => {
            error.config?.sentryTransaction?.finish();
            return Promise.reject(error);
        };
    }

    private static getResponseInterceptor401Success() {
        return (response: AxiosResponse<any, any>) => {
            return response;
        };
    }

    private static getResponseInterceptor401Error() {
        return async (error: any) => {
            const errorResponse = error.response;
            const originalRequest = error.config;
            if (errorResponse?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                const authorizationHeader: string | undefined = originalRequest.headers?.[HEADER_AUTH];
                const rejectedToken = authorizationHeader?.replace(`${stores.authStore.tokenType} `, "");

                const reloginSuccess = await stores.authStore.refreshRejectedToken(rejectedToken);
                if (reloginSuccess) {
                    console.log("Relogin durch AxiosWrapper mit refreshToken erfolgreich");
                    return Axios.authInstance?.request(originalRequest);
                }
            }
            return Promise.reject(error);
        };
    }

    /**
     * Reports backend auth (403) and server (5xx) errors to Sentry with the backend
     * response body attached. Failures on authed requests are otherwise only shown to the
     * user as a generic toast and never captured (the store catches just `console.error`),
     * so problems like the KrediFlow "Freigeben" 403 (Hasura JWSInvalidSignature / JWTExpired
     * relayed by the backend) stay invisible. 401 is intentionally not captured here — it is
     * handled by the token-refresh interceptor above.
     */
    private static getResponseInterceptorErrorCapture() {
        return (error: any) => {
            const status: number | undefined = error?.response?.status;
            if (status === 403 || (typeof status === "number" && status >= 500)) {
                Axios.captureHttpError(error, status);
            }
            return Promise.reject(error);
        };
    }

    private static captureHttpError(error: any, status: number) {
        const config = error?.config;
        const method = (config?.method ?? "").toString().toUpperCase();
        const rawUrl: string = config?.urlWithoutParams ?? config?.url ?? "unknown";
        const endpoint = rawUrl.includes(".wwportal.ch/")
            ? rawUrl.split(".wwportal.ch/")[1].split("?")[0]
            : rawUrl.split("?")[0];

        const body = error?.response?.data;
        const backendMessage =
            body && typeof body === "object"
                ? (body.error ?? JSON.stringify(body))
                : typeof body === "string" && body.length > 0
                  ? body
                  : error?.message;

        // Build a dedicated error so we control the payload and never attach the request's
        // Authorization header (which lives on the raw AxiosError's `config.headers`).
        const reportedError = new Error(`HTTP ${status} ${method} ${endpoint} — ${backendMessage}`);
        reportedError.name = "HttpRequestError";

        Sentry.captureException(reportedError, {
            level: "error",
            fingerprint: ["HttpRequestError", endpoint, String(status)],
            tags: {
                exceptionName: "HttpRequestError",
                httpStatus: String(status),
                endpoint: endpoint
            },
            user: {
                id: stores.authStore.user?.userid,
                customerid: stores.authStore.user?.customerid,
                username: stores.authStore.user?.username
            },
            extra: {
                httpStatus: status,
                method: method,
                url: rawUrl,
                backendMessage: backendMessage,
                responseBody: body
            }
        });
    }
}

export default Axios;
